/**
 * Healthcare Industry Configuration
 *
 * Comprehensive configuration for clinical engineering and medical device management
 * Covers: Diagnostic Imaging (MRI, CT, X-ray, Ultrasound), Life Support (Ventilators,
 * Defibrillators, Anesthesia), Patient Monitoring, Drug Delivery, Laboratory Equipment,
 * Surgical Systems, Sterilization, and Therapy Equipment
 *
 * Standards: Joint Commission, FDA 21 CFR 820, CMS CoP, IEC 60601, NFPA 99, AAMI
 */

import { IndustryConfig } from '../industryTypes'

export const healthcareConfig: IndustryConfig = {
  id: 'healthcare',
  name: 'Healthcare',
  description: 'Medical device management, clinical engineering, and healthcare technology',
  icon: 'Heart',

  // =============================================================================
  // TERMINOLOGY
  // =============================================================================
  terminology: {
    asset: 'Device',
    assets: 'Devices',
    assetPlural: 'Medical Devices',
    assetType: 'Device Category',
    failure: 'Malfunction',
    health: 'Status',
    healthScore: 'Device Health Score',
    healthy: 'Operational',
    degraded: 'Degraded',
    critical: 'Out of Service',
    alert: 'Clinical Alert',
    process: 'Care Delivery',
    maintenance: 'Service',
    inspection: 'Safety Inspection',
    downtime: 'Out of Service Time',
    workOrder: 'Service Request',
    prediction: 'Prediction',
    anomaly: 'Deviation',
    trend: 'Trend',
    risk: 'Patient Safety Risk',
    facility: 'Hospital',
    facilityPlural: 'Hospitals',
    location: 'Department',
    operator: 'Biomedical Technician',
    supervisor: 'Clinical Engineering Manager',
    calibration: 'Device Calibration',
    compliance: 'Regulatory Compliance',
    criticality: 'Clinical Criticality',
  },

  // =============================================================================
  // ASSET TYPES (15 comprehensive types)
  // =============================================================================
  assetTypes: [
    {
      id: 'mri_scanner',
      name: 'MRI Scanner',
      icon: 'Scan',
      description: 'Magnetic resonance imaging systems for diagnostic imaging',
      category: 'Diagnostic Imaging',
      defaultMetrics: ['helium_level', 'magnet_temperature', 'gradient_performance', 'image_quality', 'snr', 'uniformity'],
      maintenanceTypes: ['helium_fill', 'gradient_calibration', 'coil_inspection', 'cryogen_service', 'annual_pm'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Only MRI in facility', downtimeCostMultiplier: 100 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Primary imaging MRI', downtimeCostMultiplier: 50 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Backup MRI available', downtimeCostMultiplier: 20 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Research/outpatient MRI', downtimeCostMultiplier: 5 },
      ],
    },
    {
      id: 'ct_scanner',
      name: 'CT Scanner',
      icon: 'CircleDot',
      description: 'Computed tomography systems for cross-sectional imaging',
      category: 'Diagnostic Imaging',
      defaultMetrics: ['tube_heat_units', 'rotation_speed', 'detector_calibration', 'dose_output', 'image_noise', 'ct_number_accuracy'],
      maintenanceTypes: ['tube_replacement', 'detector_calibration', 'gantry_service', 'dose_check', 'annual_pm'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Emergency department CT', downtimeCostMultiplier: 150 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Primary hospital CT', downtimeCostMultiplier: 75 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Outpatient imaging CT', downtimeCostMultiplier: 25 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Research CT', downtimeCostMultiplier: 5 },
      ],
    },
    {
      id: 'xray_system',
      name: 'X-Ray System',
      icon: 'Scan',
      description: 'Radiographic and fluoroscopic imaging systems',
      category: 'Diagnostic Imaging',
      defaultMetrics: ['tube_output', 'kvp_accuracy', 'mas_accuracy', 'aec_performance', 'image_quality', 'dose_output'],
      maintenanceTypes: ['tube_replacement', 'collimator_alignment', 'aec_calibration', 'safety_check', 'annual_pm'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'OR C-arm/trauma room', downtimeCostMultiplier: 80 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'ED radiography', downtimeCostMultiplier: 40 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Routine radiography', downtimeCostMultiplier: 15 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Dental/extremity', downtimeCostMultiplier: 5 },
      ],
    },
    {
      id: 'ultrasound',
      name: 'Ultrasound System',
      icon: 'Waves',
      description: 'Diagnostic ultrasound and echocardiography systems',
      category: 'Diagnostic Imaging',
      defaultMetrics: ['image_quality', 'transducer_sensitivity', 'depth_accuracy', 'doppler_performance', 'measurement_accuracy'],
      maintenanceTypes: ['transducer_test', 'phantom_test', 'electrical_safety', 'software_update', 'annual_pm'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Cardiac/vascular lab', downtimeCostMultiplier: 60 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'OB/GYN primary', downtimeCostMultiplier: 30 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'General ultrasound', downtimeCostMultiplier: 15 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Point of care', downtimeCostMultiplier: 5 },
      ],
    },
    {
      id: 'ventilator',
      name: 'Ventilator',
      icon: 'Wind',
      description: 'Mechanical ventilation systems for respiratory support',
      category: 'Life Support',
      defaultMetrics: ['tidal_volume_accuracy', 'pressure_accuracy', 'fio2_accuracy', 'peep_accuracy', 'trigger_sensitivity', 'alarm_function'],
      maintenanceTypes: ['flow_sensor_calibration', 'valve_inspection', 'battery_test', 'circuit_test', 'annual_pm'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'ICU primary ventilator', downtimeCostMultiplier: 200 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'OR ventilator', downtimeCostMultiplier: 100 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Transport ventilator', downtimeCostMultiplier: 30 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Backup ventilator', downtimeCostMultiplier: 10 },
      ],
    },
    {
      id: 'defibrillator',
      name: 'Defibrillator',
      icon: 'HeartPulse',
      description: 'Cardiac defibrillators and AEDs for cardiac emergencies',
      category: 'Life Support',
      defaultMetrics: ['energy_delivery', 'ecg_accuracy', 'pacing_output', 'battery_capacity', 'charge_time', 'waveform_accuracy'],
      maintenanceTypes: ['energy_test', 'ecg_calibration', 'battery_replacement', 'paddle_inspection', 'annual_pm'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Code cart defibrillator', downtimeCostMultiplier: 200 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'ED/ICU defibrillator', downtimeCostMultiplier: 100 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Floor AED', downtimeCostMultiplier: 30 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Training AED', downtimeCostMultiplier: 5 },
      ],
    },
    {
      id: 'anesthesia_machine',
      name: 'Anesthesia Machine',
      icon: 'Activity',
      description: 'Anesthesia delivery and ventilation systems for surgery',
      category: 'Life Support',
      defaultMetrics: ['vaporizer_output', 'flow_accuracy', 'pressure_accuracy', 'ventilation_accuracy', 'alarm_function', 'leak_test'],
      maintenanceTypes: ['vaporizer_calibration', 'flow_sensor_cal', 'bellows_inspection', 'leak_test', 'annual_pm'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'OR primary unit', downtimeCostMultiplier: 150 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Cath lab/procedure', downtimeCostMultiplier: 80 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Backup unit', downtimeCostMultiplier: 25 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'MRI-compatible', downtimeCostMultiplier: 15 },
      ],
    },
    {
      id: 'patient_monitor',
      name: 'Patient Monitor',
      icon: 'HeartPulse',
      description: 'Multi-parameter vital signs monitoring systems',
      category: 'Patient Monitoring',
      defaultMetrics: ['ecg_quality', 'spo2_accuracy', 'nibp_accuracy', 'temperature_accuracy', 'etco2_accuracy', 'alarm_function'],
      maintenanceTypes: ['ecg_calibration', 'spo2_cal', 'nibp_cal', 'battery_replacement', 'annual_pm'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'ICU/CCU monitor', downtimeCostMultiplier: 75 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'OR monitor', downtimeCostMultiplier: 40 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Telemetry', downtimeCostMultiplier: 15 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Spot check/transport', downtimeCostMultiplier: 5 },
      ],
    },
    {
      id: 'infusion_pump',
      name: 'Infusion Pump',
      icon: 'Droplet',
      description: 'IV infusion delivery systems for medication administration',
      category: 'Drug Delivery',
      defaultMetrics: ['flow_accuracy', 'occlusion_detection', 'air_detection', 'battery_health', 'alarm_function', 'pressure_limits'],
      maintenanceTypes: ['flow_calibration', 'sensor_test', 'battery_replacement', 'firmware_update', 'annual_pm'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'High-risk medication pump', downtimeCostMultiplier: 50 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'ICU/OR pump', downtimeCostMultiplier: 25 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Floor pump', downtimeCostMultiplier: 8 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Ambulatory pump', downtimeCostMultiplier: 3 },
      ],
    },
    {
      id: 'syringe_pump',
      name: 'Syringe Pump',
      icon: 'Syringe',
      description: 'Precision syringe-based infusion for critical medications',
      category: 'Drug Delivery',
      defaultMetrics: ['flow_accuracy', 'occlusion_detection', 'syringe_recognition', 'bolus_accuracy', 'alarm_function'],
      maintenanceTypes: ['flow_calibration', 'drive_inspection', 'sensor_cal', 'battery_test', 'annual_pm'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'NICU/vasoactive meds', downtimeCostMultiplier: 60 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'ICU syringe pump', downtimeCostMultiplier: 30 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Anesthesia pump', downtimeCostMultiplier: 15 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'General use', downtimeCostMultiplier: 5 },
      ],
    },
    {
      id: 'lab_analyzer',
      name: 'Laboratory Analyzer',
      icon: 'TestTube2',
      description: 'Clinical chemistry, hematology, and immunoassay analyzers',
      category: 'Laboratory',
      defaultMetrics: ['qc_performance', 'throughput', 'reagent_stability', 'calibration_drift', 'carryover', 'precision'],
      maintenanceTypes: ['reagent_calibration', 'probe_cleaning', 'fluidics_service', 'sensor_replacement', 'annual_pm'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'STAT analyzer', downtimeCostMultiplier: 100 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Primary core analyzer', downtimeCostMultiplier: 50 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Backup analyzer', downtimeCostMultiplier: 15 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Specialized/reference', downtimeCostMultiplier: 5 },
      ],
    },
    {
      id: 'dialysis_machine',
      name: 'Dialysis Machine',
      icon: 'Droplets',
      description: 'Hemodialysis and peritoneal dialysis systems',
      category: 'Therapy',
      defaultMetrics: ['blood_flow_accuracy', 'ultrafiltration_rate', 'conductivity', 'temperature_accuracy', 'blood_leak_detection'],
      maintenanceTypes: ['flow_calibration', 'disinfection', 'filter_replacement', 'sensor_cal', 'annual_pm'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Acute dialysis/ICU', downtimeCostMultiplier: 80 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Chronic dialysis primary', downtimeCostMultiplier: 40 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Backup unit', downtimeCostMultiplier: 15 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Home dialysis', downtimeCostMultiplier: 8 },
      ],
    },
    {
      id: 'surgical_robot',
      name: 'Surgical Robot',
      icon: 'Bot',
      description: 'Robotic surgical systems for minimally invasive surgery',
      category: 'Surgical',
      defaultMetrics: ['arm_accuracy', 'instrument_calibration', 'vision_quality', 'motion_tracking', 'safety_system'],
      maintenanceTypes: ['arm_calibration', 'instrument_service', 'vision_alignment', 'software_update', 'annual_pm'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Single robot facility', downtimeCostMultiplier: 200 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Primary surgical robot', downtimeCostMultiplier: 100 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Backup robot', downtimeCostMultiplier: 40 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Training robot', downtimeCostMultiplier: 10 },
      ],
    },
    {
      id: 'sterilizer',
      name: 'Sterilizer',
      icon: 'Flame',
      description: 'Steam, EtO, and low-temperature sterilization systems',
      category: 'Sterile Processing',
      defaultMetrics: ['temperature_accuracy', 'pressure_accuracy', 'cycle_time', 'bi_results', 'steam_quality'],
      maintenanceTypes: ['bi_testing', 'valve_service', 'gasket_replacement', 'sensor_cal', 'annual_pm'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Primary OR sterilizer', downtimeCostMultiplier: 120 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'CSSD primary', downtimeCostMultiplier: 60 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Backup sterilizer', downtimeCostMultiplier: 20 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Tabletop/flash', downtimeCostMultiplier: 8 },
      ],
    },
    {
      id: 'electrosurgical_unit',
      name: 'Electrosurgical Unit',
      icon: 'Zap',
      description: 'ESU and harmonic devices for surgical cutting and coagulation',
      category: 'Surgical',
      defaultMetrics: ['power_output_accuracy', 'leakage_current', 'alarm_function', 'activation_response', 'electrode_condition'],
      maintenanceTypes: ['power_calibration', 'safety_test', 'electrode_inspection', 'cable_test', 'annual_pm'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Main OR ESU', downtimeCostMultiplier: 50 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Specialty OR ESU', downtimeCostMultiplier: 30 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Procedure room', downtimeCostMultiplier: 15 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Backup ESU', downtimeCostMultiplier: 5 },
      ],
    },
  ],

  // =============================================================================
  // KPI DOMAINS (8 comprehensive domains)
  // =============================================================================
  kpiDomains: [
    {
      id: 'patient_safety',
      name: 'Patient Safety',
      icon: 'ShieldCheck',
      description: 'Device safety metrics impacting patient care quality',
      businessValue: 'Ensure patient safety and reduce adverse events',
      category: 'industry_specific',
      metrics: [
        { id: 'alarm_accuracy', name: 'Alarm Accuracy', unit: '%', description: 'True positive alarm rate', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 98, warning: 95, critical: 90 } },
        { id: 'measurement_accuracy', name: 'Measurement Accuracy', unit: '%', description: 'Within manufacturer spec', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 99, warning: 97, critical: 95 } },
        { id: 'safety_incidents', name: 'Safety Incidents', unit: 'count', description: 'Device-related safety events', dataType: 'discrete', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 0, warning: 1, critical: 3 } },
        { id: 'recall_compliance', name: 'Recall Compliance', unit: '%', description: 'FDA recall remediation complete', dataType: 'discrete', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 95, critical: 90 } },
      ],
      thresholds: { healthy: { min: 98 }, warning: { min: 95, max: 98 }, critical: { max: 95 } },
      visualizations: ['gauge', 'trend', 'bar_chart', 'comparison'],
    },
    {
      id: 'device_availability',
      name: 'Device Availability',
      icon: 'Clock',
      description: 'Device uptime and reliability metrics',
      businessValue: 'Maximize device availability for patient care',
      category: 'universal',
      metrics: [
        { id: 'uptime', name: 'Uptime', unit: '%', description: 'Operational vs scheduled time', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 98, warning: 95, critical: 90 } },
        { id: 'mtbf', name: 'Mean Time Between Failures', unit: 'days', description: 'Average time between malfunctions', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 180, warning: 90, critical: 30 } },
        { id: 'mttr', name: 'Mean Time To Repair', unit: 'hours', description: 'Average repair/service time', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 4, warning: 8, critical: 24 } },
        { id: 'first_time_fix', name: 'First Time Fix Rate', unit: '%', description: 'Issues resolved on first visit', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 90, warning: 80, critical: 70 } },
      ],
      thresholds: { healthy: { min: 98 }, warning: { min: 95, max: 98 }, critical: { max: 95 } },
      visualizations: ['gauge', 'line_chart', 'comparison', 'trend'],
    },
    {
      id: 'calibration_compliance',
      name: 'Calibration & Compliance',
      icon: 'ClipboardCheck',
      description: 'Device calibration status and regulatory compliance',
      businessValue: 'Ensure measurement accuracy and regulatory compliance',
      category: 'industry_specific',
      metrics: [
        { id: 'calibration_current', name: 'Calibration Current', unit: '%', description: 'Devices with current calibration', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 98, critical: 95 } },
        { id: 'pm_compliance', name: 'PM Compliance', unit: '%', description: 'PM completed on schedule', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 95, critical: 90 } },
        { id: 'safety_inspection_current', name: 'Safety Inspections Current', unit: '%', description: 'Electrical safety tests current', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 98, critical: 95 } },
        { id: 'documentation_complete', name: 'Documentation Complete', unit: '%', description: 'Service records complete', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 95, critical: 90 } },
      ],
      thresholds: { healthy: { min: 100 }, warning: { min: 95, max: 100 }, critical: { max: 95 } },
      visualizations: ['gauge', 'bar_chart', 'comparison', 'heatmap'],
    },
    {
      id: 'imaging_quality',
      name: 'Imaging Quality',
      icon: 'Scan',
      description: 'Diagnostic imaging system performance metrics',
      businessValue: 'Ensure diagnostic image quality for accurate diagnoses',
      category: 'industry_specific',
      metrics: [
        { id: 'image_quality_score', name: 'Image Quality Score', unit: 'points', description: 'ACR phantom scoring', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 95, warning: 85, critical: 70 } },
        { id: 'snr', name: 'Signal to Noise Ratio', unit: 'dB', description: 'Image SNR measurement', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 40, warning: 30, critical: 20 } },
        { id: 'geometric_accuracy', name: 'Geometric Accuracy', unit: 'mm', description: 'Spatial accuracy', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 1, warning: 2, critical: 3 } },
        { id: 'dose_index', name: 'Radiation Dose Index', unit: 'mGy', description: 'Patient radiation dose', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 10, warning: 15, critical: 20 } },
      ],
      thresholds: { healthy: { min: 90 }, warning: { min: 70, max: 90 }, critical: { max: 70 } },
      visualizations: ['gauge', 'trend', 'bar_chart', 'radar'],
    },
    {
      id: 'life_support_performance',
      name: 'Life Support Performance',
      icon: 'HeartPulse',
      description: 'Critical care and life support equipment performance',
      businessValue: 'Ensure reliable operation of life-critical devices',
      category: 'industry_specific',
      metrics: [
        { id: 'ventilator_accuracy', name: 'Ventilator Accuracy', unit: '%', description: 'Tidal volume/pressure accuracy', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 98, warning: 95, critical: 90 } },
        { id: 'defibrillator_energy', name: 'Defibrillator Energy Accuracy', unit: '%', description: 'Delivered vs selected energy', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 98, warning: 95, critical: 90 } },
        { id: 'infusion_accuracy', name: 'Infusion Accuracy', unit: '%', description: 'Flow rate accuracy', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 97, warning: 95, critical: 90 } },
        { id: 'battery_health', name: 'Battery Health', unit: '%', description: 'Battery capacity vs new', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 80, warning: 60, critical: 40 } },
      ],
      thresholds: { healthy: { min: 95 }, warning: { min: 90, max: 95 }, critical: { max: 90 } },
      visualizations: ['gauge', 'line_chart', 'distribution', 'comparison'],
    },
    {
      id: 'utilization',
      name: 'Device Utilization',
      icon: 'BarChart3',
      description: 'How effectively devices are being utilized',
      businessValue: 'Optimize device utilization and capital efficiency',
      category: 'universal',
      metrics: [
        { id: 'utilization_rate', name: 'Utilization Rate', unit: '%', description: 'In-use vs available time', dataType: 'continuous', aggregation: 'avg', goodDirection: 'target', defaultThresholds: { healthy: 75, warning: 50, critical: 30 } },
        { id: 'procedures_per_day', name: 'Procedures/Day', unit: 'count', description: 'Average procedures per device per day', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 20, warning: 15, critical: 10 } },
        { id: 'patient_throughput', name: 'Patient Throughput', unit: 'patients/hr', description: 'Patients served per hour', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 4, warning: 3, critical: 2 } },
        { id: 'idle_time', name: 'Idle Time', unit: '%', description: 'Time device is idle when available', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 20, warning: 40, critical: 60 } },
      ],
      thresholds: { healthy: { min: 70 }, warning: { min: 50, max: 70 }, critical: { max: 50 } },
      visualizations: ['line_chart', 'bar_chart', 'heatmap', 'comparison'],
    },
    {
      id: 'laboratory_quality',
      name: 'Laboratory Quality',
      icon: 'TestTube2',
      description: 'Clinical laboratory analyzer performance',
      businessValue: 'Ensure accurate lab results for patient diagnosis',
      category: 'industry_specific',
      metrics: [
        { id: 'qc_pass_rate', name: 'QC Pass Rate', unit: '%', description: 'Quality control passing rate', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 98, warning: 95, critical: 90 } },
        { id: 'cv_performance', name: 'CV Performance', unit: '%', description: 'Coefficient of variation', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 3, warning: 5, critical: 10 } },
        { id: 'tat_compliance', name: 'TAT Compliance', unit: '%', description: 'Turnaround time compliance', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 95, warning: 90, critical: 80 } },
        { id: 'reagent_efficiency', name: 'Reagent Efficiency', unit: '%', description: 'Tests per reagent vs expected', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 95, warning: 85, critical: 75 } },
      ],
      thresholds: { healthy: { min: 95 }, warning: { min: 90, max: 95 }, critical: { max: 90 } },
      visualizations: ['gauge', 'trend', 'distribution', 'comparison'],
    },
    {
      id: 'cost_management',
      name: 'Cost Management',
      icon: 'DollarSign',
      description: 'Device service and operating cost metrics',
      businessValue: 'Optimize clinical engineering costs',
      category: 'universal',
      metrics: [
        { id: 'cost_per_device', name: 'Annual Cost Per Device', unit: '$', description: 'Total service cost per device', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 500, warning: 1000, critical: 2000 } },
        { id: 'parts_cost_ratio', name: 'Parts Cost Ratio', unit: '%', description: 'Parts vs labor cost', dataType: 'continuous', aggregation: 'avg', goodDirection: 'target', defaultThresholds: { healthy: 40, warning: 60, critical: 80 } },
        { id: 'oem_vs_inhouse', name: 'In-House vs OEM', unit: '%', description: 'In-house service percentage', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 80, warning: 60, critical: 40 } },
        { id: 'warranty_recovery', name: 'Warranty Recovery', unit: '%', description: 'Warranty claims recovered', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 90, warning: 75, critical: 50 } },
      ],
      thresholds: { healthy: { min: 0 }, warning: { min: 0 }, critical: { min: 0 } },
      visualizations: ['bar_chart', 'trend', 'comparison', 'distribution'],
    },
  ],

  // =============================================================================
  // MAINTENANCE TYPES (12 comprehensive types)
  // =============================================================================
  maintenanceTypes: [
    {
      id: 'annual_pm',
      name: 'Annual Preventive Maintenance',
      description: 'Comprehensive annual PM per manufacturer specifications and TJC/CMS requirements',
      category: 'preventive',
      defaultInterval: { value: 12, unit: 'months' },
      laborRequirements: [
        { role: 'Biomedical Technician', count: 1, hourlyRate: 55 },
      ],
      estimatedDuration: { min: 1, typical: 2, max: 4, unit: 'hours' },
      skillsRequired: ['biomedical_technology', 'electrical_safety', 'device_specific'],
      partsCommonly: ['filters', 'batteries', 'sensors', 'cables', 'disposables'],
    },
    {
      id: 'semiannual_pm',
      name: 'Semi-Annual Preventive Maintenance',
      description: 'Bi-annual PM for high-risk and high-use devices',
      category: 'preventive',
      defaultInterval: { value: 6, unit: 'months' },
      laborRequirements: [
        { role: 'Biomedical Technician', count: 1, hourlyRate: 55 },
      ],
      estimatedDuration: { min: 30, typical: 60, max: 120, unit: 'minutes' },
      skillsRequired: ['biomedical_technology', 'device_specific'],
      partsCommonly: ['filters', 'consumables'],
    },
    {
      id: 'calibration',
      name: 'Calibration & Verification',
      description: 'Precision calibration and accuracy verification with NIST-traceable standards',
      category: 'condition_based',
      defaultInterval: { value: 12, unit: 'months' },
      laborRequirements: [
        { role: 'Calibration Specialist', count: 1, hourlyRate: 65 },
      ],
      estimatedDuration: { min: 30, typical: 60, max: 120, unit: 'minutes' },
      skillsRequired: ['calibration', 'metrology', 'documentation'],
      partsCommonly: ['calibration_standards'],
    },
    {
      id: 'electrical_safety',
      name: 'Electrical Safety Test',
      description: 'IEC 62353 electrical safety testing for patient and operator protection',
      category: 'preventive',
      defaultInterval: { value: 12, unit: 'months' },
      laborRequirements: [
        { role: 'Biomedical Technician', count: 1, hourlyRate: 55 },
      ],
      estimatedDuration: { min: 15, typical: 30, max: 60, unit: 'minutes' },
      skillsRequired: ['electrical_safety', 'iec_62353'],
      partsCommonly: [],
    },
    {
      id: 'corrective_repair',
      name: 'Corrective Repair',
      description: 'Repair of malfunctioning device to restore functionality',
      category: 'corrective',
      laborRequirements: [
        { role: 'Biomedical Technician', count: 1, hourlyRate: 55 },
      ],
      estimatedDuration: { min: 1, typical: 4, max: 24, unit: 'hours' },
      skillsRequired: ['troubleshooting', 'repair', 'device_specific'],
      partsCommonly: ['boards', 'motors', 'sensors', 'displays', 'power_supplies'],
    },
    {
      id: 'oem_service',
      name: 'OEM Service',
      description: 'Manufacturer service for complex repairs and specialized maintenance',
      category: 'corrective',
      laborRequirements: [
        { role: 'OEM Field Service Engineer', count: 1, hourlyRate: 175 },
      ],
      estimatedDuration: { min: 2, typical: 8, max: 40, unit: 'hours' },
      skillsRequired: ['oem_certified', 'specialized_equipment'],
      partsCommonly: ['oem_parts', 'proprietary_components'],
    },
    {
      id: 'software_update',
      name: 'Software/Firmware Update',
      description: 'Device software and firmware updates for security and functionality',
      category: 'preventive',
      laborRequirements: [
        { role: 'Biomedical Technician', count: 1, hourlyRate: 55 },
        { role: 'IT Specialist', count: 1, hourlyRate: 65 },
      ],
      estimatedDuration: { min: 30, typical: 60, max: 180, unit: 'minutes' },
      skillsRequired: ['software_installation', 'network_configuration', 'device_specific'],
      partsCommonly: ['software_licenses'],
    },
    {
      id: 'battery_replacement',
      name: 'Battery Replacement',
      description: 'Replacement of device batteries per lifecycle requirements',
      category: 'preventive',
      defaultInterval: { value: 24, unit: 'months' },
      laborRequirements: [
        { role: 'Biomedical Technician', count: 1, hourlyRate: 55 },
      ],
      estimatedDuration: { min: 15, typical: 30, max: 60, unit: 'minutes' },
      skillsRequired: ['battery_replacement', 'device_specific'],
      partsCommonly: ['batteries', 'battery_contacts'],
    },
    {
      id: 'imaging_qc',
      name: 'Imaging Quality Control',
      description: 'ACR/AAPM phantom testing and image quality verification',
      category: 'condition_based',
      defaultInterval: { value: 1, unit: 'months' },
      laborRequirements: [
        { role: 'Imaging Specialist', count: 1, hourlyRate: 70 },
      ],
      estimatedDuration: { min: 30, typical: 60, max: 120, unit: 'minutes' },
      skillsRequired: ['phantom_testing', 'image_analysis', 'modality_specific'],
      partsCommonly: ['phantoms', 'test_objects'],
    },
    {
      id: 'sterilizer_bi_test',
      name: 'Biological Indicator Testing',
      description: 'Routine BI testing for sterilization efficacy verification',
      category: 'condition_based',
      defaultInterval: { value: 1, unit: 'weeks' },
      laborRequirements: [
        { role: 'Sterile Processing Technician', count: 1, hourlyRate: 35 },
      ],
      estimatedDuration: { min: 15, typical: 30, max: 60, unit: 'minutes' },
      skillsRequired: ['bi_testing', 'sterile_processing'],
      partsCommonly: ['biological_indicators', 'chemical_indicators'],
    },
    {
      id: 'ventilator_circuit_test',
      name: 'Ventilator Circuit Test',
      description: 'Full ventilator circuit and patient breathing system test',
      category: 'condition_based',
      defaultInterval: { value: 3, unit: 'months' },
      laborRequirements: [
        { role: 'Life Support Specialist', count: 1, hourlyRate: 65 },
      ],
      estimatedDuration: { min: 30, typical: 60, max: 90, unit: 'minutes' },
      skillsRequired: ['ventilator_testing', 'flow_analysis', 'alarm_testing'],
      partsCommonly: ['test_lungs', 'flow_sensors', 'filters'],
    },
    {
      id: 'incoming_inspection',
      name: 'Incoming Inspection',
      description: 'New device acceptance testing and clinical readiness verification',
      category: 'preventive',
      laborRequirements: [
        { role: 'Biomedical Engineer', count: 1, hourlyRate: 85 },
        { role: 'Biomedical Technician', count: 1, hourlyRate: 55 },
      ],
      estimatedDuration: { min: 2, typical: 4, max: 8, unit: 'hours' },
      skillsRequired: ['acceptance_testing', 'documentation', 'asset_management'],
      partsCommonly: ['accessories', 'consumables'],
    },
  ],

  // =============================================================================
  // RISK FACTORS
  // =============================================================================
  riskFactors: [
    { id: 'patient_safety_risk', name: 'Patient Safety Risk', description: 'Risk of patient harm from device malfunction', category: 'safety', weight: 0.40, calculationMethod: 'exponential', inputs: ['device_class', 'failure_mode', 'redundancy', 'function_criticality'] },
    { id: 'operational_risk', name: 'Operational Risk', description: 'Risk of clinical service disruption', category: 'operational', weight: 0.25, calculationMethod: 'linear', inputs: ['uptime', 'backup_availability', 'utilization'] },
    { id: 'compliance_risk', name: 'Compliance Risk', description: 'Risk of regulatory non-compliance', category: 'compliance', weight: 0.20, calculationMethod: 'threshold', inputs: ['calibration_status', 'pm_status', 'recall_status', 'documentation'] },
    { id: 'financial_risk', name: 'Financial Risk', description: 'Risk of unexpected repair costs', category: 'financial', weight: 0.10, calculationMethod: 'linear', inputs: ['age', 'repair_history', 'warranty_status', 'parts_availability'] },
    { id: 'cybersecurity_risk', name: 'Cybersecurity Risk', description: 'Risk of device security vulnerability', category: 'compliance', weight: 0.05, calculationMethod: 'threshold', inputs: ['patch_status', 'network_exposure', 'authentication'] },
  ],

  // =============================================================================
  // COMPLIANCE STANDARDS (8 comprehensive standards)
  // =============================================================================
  complianceStandards: [
    {
      id: 'joint_commission',
      name: 'Joint Commission Standards',
      shortName: 'TJC',
      description: 'Healthcare organization accreditation standards for equipment management',
      authority: 'The Joint Commission',
      region: ['US'],
      requirements: [
        { id: 'ec_02_04_01', name: 'Equipment Management Plan', description: 'Documented medical equipment management program', kpiId: 'documentation_complete', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'ec_02_04_03', name: 'Equipment Maintenance', description: 'Risk-based maintenance strategy', kpiId: 'pm_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'ec_02_06_01', name: 'Clinical Alarm Safety', description: 'Alarm management program', kpiId: 'alarm_management', threshold: 100, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'quarterly',
    },
    {
      id: 'cms_cop',
      name: 'CMS Conditions of Participation',
      shortName: 'CMS CoP',
      description: 'Medicare/Medicaid participation requirements',
      authority: 'Centers for Medicare & Medicaid Services',
      region: ['US'],
      requirements: [
        { id: 'equipment_maintenance', name: 'Equipment Maintenance', description: 'Properly maintained equipment', kpiId: 'pm_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'safety_environment', name: 'Safe Environment', description: 'Equipment safe for patients', kpiId: 'electrical_safety_current', threshold: 100, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'annual',
    },
    {
      id: 'fda_qsr',
      name: 'FDA Quality System Regulation',
      shortName: 'FDA 21 CFR 820',
      description: 'Medical device quality system requirements',
      authority: 'Food and Drug Administration',
      region: ['US'],
      requirements: [
        { id: 'calibration', name: 'Calibration Control', description: 'Calibrated measurement equipment', kpiId: 'calibration_current', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'records', name: 'Device History Record', description: 'Complete service documentation', kpiId: 'documentation_complete', threshold: 100, operator: 'eq', severity: 'required' },
        { id: 'complaints', name: 'Complaint Handling', description: 'MDR reporting compliance', kpiId: 'mdr_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'annual',
    },
    {
      id: 'iec_60601',
      name: 'IEC 60601 Medical Electrical Equipment',
      shortName: 'IEC 60601',
      description: 'International safety standards for medical electrical equipment',
      authority: 'International Electrotechnical Commission',
      region: ['Global'],
      requirements: [
        { id: 'leakage_current', name: 'Leakage Current Limits', description: 'Patient and enclosure leakage limits', kpiId: 'leakage_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'grounding', name: 'Protective Earth', description: 'Grounding integrity', kpiId: 'ground_impedance', threshold: 100, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'annual',
    },
    {
      id: 'iec_62353',
      name: 'IEC 62353 Recurrent Testing',
      shortName: 'IEC 62353',
      description: 'Recurrent test and test after repair of medical electrical equipment',
      authority: 'International Electrotechnical Commission',
      region: ['Global'],
      requirements: [
        { id: 'periodic_test', name: 'Periodic Electrical Safety', description: 'Annual electrical safety testing', kpiId: 'safety_inspection_current', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'post_repair_test', name: 'Post-Repair Testing', description: 'Safety test after service', kpiId: 'post_repair_test', threshold: 100, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'annual',
    },
    {
      id: 'nfpa_99',
      name: 'NFPA 99 Health Care Facilities Code',
      shortName: 'NFPA 99',
      description: 'Fire and safety requirements for healthcare facilities',
      authority: 'National Fire Protection Association',
      region: ['US'],
      requirements: [
        { id: 'electrical_systems', name: 'Electrical Systems', description: 'Medical electrical system requirements', kpiId: 'electrical_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'medical_gas', name: 'Medical Gas Systems', description: 'Medical gas and vacuum requirements', kpiId: 'gas_system_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'annual',
    },
    {
      id: 'aami',
      name: 'AAMI Standards',
      shortName: 'AAMI',
      description: 'Association for Advancement of Medical Instrumentation standards',
      authority: 'AAMI',
      region: ['US', 'Global'],
      requirements: [
        { id: 'infusion_pump', name: 'Infusion Pump Testing', description: 'AAMI TIR35 infusion pump testing', kpiId: 'infusion_accuracy', threshold: 95, operator: 'gte', severity: 'required' },
        { id: 'sterilization', name: 'Sterilization', description: 'Steam sterilization requirements', kpiId: 'sterilization_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'annual',
    },
    {
      id: 'hipaa',
      name: 'HIPAA Security Rule',
      shortName: 'HIPAA',
      description: 'Protected health information security for networked devices',
      authority: 'Health and Human Services',
      region: ['US'],
      requirements: [
        { id: 'access_control', name: 'Access Control', description: 'Device access controls', kpiId: 'access_control_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'encryption', name: 'Encryption', description: 'Data encryption on devices', kpiId: 'encryption_compliance', threshold: 100, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'annual',
    },
  ],

  // =============================================================================
  // PROFESSIONAL ROLES — patient-centric clinical roles first, then device /
  // clinical-engineering roles. The healthcare module's unit-of-analysis is
  // the patient (vitals trajectory, clinical pathway, readmission risk,
  // length-of-stay) — biomedical devices are supporting infrastructure.
  // =============================================================================
  professionalRoles: [
    // Patient-centric clinical roles
    {
      id: 'hospitalist',
      category: 'sme' as const,
      name: 'Hospitalist / Attending Physician',
      shortName: 'Hospitalist',
      description: 'Inpatient physician owning the clinical pathway, treatment plan, and disposition for admitted patients',
      responsibilities: [
        'Admission orders + treatment plan authoring',
        'Daily rounding and progress-note documentation',
        'Discharge planning and disposition (home / SNF / hospice)',
        'Clinical pathway adherence + readmission-risk review',
        'Interdisciplinary consult coordination',
      ],
      focusAreas: ['clinical_pathway', 'readmission_risk', 'length_of_stay', 'mortality_risk', 'order_completeness'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'risk', 'action_items'] as const,
      },
      decisionAuthority: ['Diagnostic orders', 'Therapeutic interventions', 'Discharge timing', 'Code status changes'],
      reportingTo: 'Department Chief',
      icon: 'Stethoscope',
    },
    {
      id: 'nurse_manager',
      category: 'executive' as const,
      name: 'Nurse Manager (Unit Lead)',
      shortName: 'NM',
      description: 'Frontline clinical-operations leader: staffing, throughput, patient safety, family communication on a hospital unit',
      responsibilities: [
        'Unit staffing + acuity-based assignments',
        'Patient throughput + bed-turnover coordination',
        'Safety event reviews (falls / pressure injuries / sepsis bundles)',
        'Family communication + complaint resolution',
        'Compliance with The Joint Commission + CMS Conditions of Participation',
      ],
      focusAreas: ['unit_acuity', 'turnover_minutes', 'safety_events', 'patient_satisfaction_HCAHPS', 'nurse_to_patient_ratio'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['business_impact', 'risk', 'action_items'] as const,
      },
      decisionAuthority: ['Staffing changes', 'Bed assignment', 'Escalation to rapid response'],
      reportingTo: 'Director of Nursing',
      icon: 'UserCog',
    },
    {
      id: 'clinical_quality_lead',
      category: 'analyst' as const,
      name: 'Clinical Quality / Patient Safety Lead',
      shortName: 'CQ Lead',
      description: 'Owns clinical-outcome quality programs: sepsis bundles, CLABSI / CAUTI prevention, readmission reduction, mortality review',
      responsibilities: [
        'Quality measure abstraction (CMS / HEDIS / Joint Commission)',
        'Sepsis / VTE / SCIP / CAUTI bundle adherence tracking',
        'Mortality + readmission case review',
        'Performance-improvement projects (PDSA / DMAIC)',
        'Root-cause analysis for serious safety events',
      ],
      focusAreas: ['readmission_rate', 'mortality_index', 'safety_event_rate', 'bundle_compliance', 'HCAHPS'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['risk', 'business_impact', 'action_items'] as const,
      },
      decisionAuthority: ['Quality program scope', 'Improvement project prioritisation', 'Reportable event escalation'],
      reportingTo: 'Chief Medical Officer / VP Quality',
      icon: 'ClipboardCheck',
    },
    // Device / clinical-engineering roles
    {
      id: 'clinical_engineering_director',
      category: 'executive' as const,
      name: 'Clinical Engineering Director',
      shortName: 'CE Director',
      description: 'Leads clinical engineering department and strategic initiatives',
      responsibilities: ['Department leadership', 'Budget management', 'Vendor management', 'Capital planning', 'Regulatory compliance'],
      focusAreas: ['Department KPIs', 'Cost management', 'Strategic planning', 'Accreditation'],
      explanationStyle: {
        depth: 'high_level' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'executive' as const,
        focusOn: ['business_impact', 'risk', 'action_items'] as const,
      },
      decisionAuthority: ['Capital purchases', 'Service contracts', 'Staffing', 'Policy approval'],
      reportingTo: 'VP of Operations',
      icon: 'Building',
    },
    {
      id: 'clinical_engineer',
      category: 'sme' as const,
      name: 'Clinical Engineer',
      shortName: 'CCE',
      description: 'Professional engineer managing medical technology programs',
      responsibilities: ['Technology assessment', 'Equipment planning', 'Project management', 'Quality improvement'],
      focusAreas: ['Technology lifecycle', 'Risk management', 'Process improvement', 'Standards compliance'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['technical_details', 'compliance', 'action_items', 'risk'] as const,
      },
      decisionAuthority: ['Equipment specifications', 'Service strategies', 'Vendor evaluation'],
      reportingTo: 'Clinical Engineering Director',
      icon: 'Stethoscope',
    },
    {
      id: 'biomedical_engineer',
      category: 'sme' as const,
      name: 'Biomedical Engineer',
      shortName: 'BME',
      description: 'Technical specialist for complex medical device management',
      responsibilities: ['Complex troubleshooting', 'System integration', 'Technical training', 'Protocol development'],
      focusAreas: ['Device performance', 'Integration', 'Advanced diagnostics', 'Training'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'compliance', 'action_items'] as const,
      },
      decisionAuthority: ['Repair strategies', 'Parts selection', 'Protocol approval'],
      reportingTo: 'Clinical Engineer',
      icon: 'Microscope',
    },
    {
      id: 'bmet_iii',
      category: 'technician' as const,
      name: 'BMET III / Senior Technician',
      shortName: 'BMET III',
      description: 'Senior biomedical equipment technician with advanced skills',
      responsibilities: ['Complex repairs', 'Mentoring', 'Quality assurance', 'Specialty systems'],
      focusAreas: ['Advanced diagnostics', 'Specialty equipment', 'Training others', 'Quality'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: false,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'action_items'] as const,
      },
      decisionAuthority: ['Repair methods', 'Parts replacement', 'Work assignments'],
      reportingTo: 'Biomedical Engineer',
      icon: 'Wrench',
    },
    {
      id: 'bmet_ii',
      category: 'technician' as const,
      name: 'BMET II / Technician',
      shortName: 'BMET II',
      description: 'Experienced biomedical equipment technician',
      responsibilities: ['PM execution', 'Corrective repairs', 'Calibration', 'Documentation'],
      focusAreas: ['Routine service', 'PM compliance', 'Work order completion', 'Documentation'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: false,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'action_items'] as const,
      },
      decisionAuthority: ['Routine repairs', 'Parts requests'],
      reportingTo: 'BMET III',
      icon: 'Wrench',
    },
    {
      id: 'bmet_i',
      category: 'technician' as const,
      name: 'BMET I / Associate Technician',
      shortName: 'BMET I',
      description: 'Entry-level biomedical equipment technician',
      responsibilities: ['Basic PM', 'Electrical safety testing', 'Incoming inspection', 'Documentation'],
      focusAreas: ['Learning', 'Basic service', 'Safety testing', 'Inventory'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: false,
        includeCosts: false,
        includeRootCause: false,
        includeProcedures: true,
        includeTimelines: false,
        vocabularyLevel: 'technical' as const,
        focusOn: ['action_items'] as const,
      },
      decisionAuthority: ['Escalation to senior tech'],
      reportingTo: 'BMET II',
      icon: 'User',
    },
    {
      id: 'imaging_specialist',
      category: 'sme' as const,
      name: 'Imaging Specialist',
      shortName: 'Imaging Spec',
      description: 'Specialist in diagnostic imaging equipment service',
      responsibilities: ['Imaging PM', 'Image quality testing', 'Dose management', 'ACR compliance'],
      focusAreas: ['Image quality', 'Radiation safety', 'Modality expertise', 'Regulatory'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'compliance', 'action_items'] as const,
      },
      decisionAuthority: ['Image quality decisions', 'Dose optimization', 'QC protocols'],
      reportingTo: 'Biomedical Engineer',
      icon: 'Scan',
    },
    {
      id: 'life_support_specialist',
      category: 'sme' as const,
      name: 'Life Support Specialist',
      shortName: 'LS Spec',
      description: 'Specialist in critical care and life support equipment',
      responsibilities: ['Ventilator service', 'Defibrillator PM', 'Anesthesia service', 'Critical equipment'],
      focusAreas: ['Patient safety', 'Life support accuracy', 'Alarm management', 'Battery management'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'risk', 'action_items'] as const,
      },
      decisionAuthority: ['Life support service decisions', 'Safety protocols'],
      reportingTo: 'Biomedical Engineer',
      icon: 'HeartPulse',
    },
    {
      id: 'it_integration_specialist',
      category: 'sme' as const,
      name: 'Medical IT Integration Specialist',
      shortName: 'IT Spec',
      description: 'Specialist in medical device networking and integration',
      responsibilities: ['Network integration', 'HL7/DICOM', 'Cybersecurity', 'System interfaces'],
      focusAreas: ['Network connectivity', 'Data integration', 'Security patching', 'Interoperability'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'compliance', 'action_items'] as const,
      },
      decisionAuthority: ['Network configurations', 'Security settings', 'Integration architecture'],
      reportingTo: 'Clinical Engineer',
      icon: 'Network',
    },
    {
      id: 'calibration_specialist',
      category: 'technician' as const,
      name: 'Calibration Specialist',
      shortName: 'Cal Spec',
      description: 'Specialist in precision calibration and metrology',
      responsibilities: ['Device calibration', 'Standards maintenance', 'Uncertainty analysis', 'Documentation'],
      focusAreas: ['Calibration accuracy', 'Traceability', 'Standards compliance', 'Uncertainty'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: false,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'compliance', 'action_items'] as const,
      },
      decisionAuthority: ['Calibration intervals', 'Standards selection', 'Pass/fail determination'],
      reportingTo: 'Biomedical Engineer',
      icon: 'Target',
    },
    {
      id: 'asset_manager',
      category: 'analyst' as const,
      name: 'Medical Equipment Asset Manager',
      shortName: 'Asset Mgr',
      description: 'Manages medical equipment inventory and lifecycle',
      responsibilities: ['Asset tracking', 'Lifecycle management', 'Depreciation', 'Disposal'],
      focusAreas: ['Inventory accuracy', 'Capital planning', 'Replacement analysis', 'Cost tracking'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['business_impact', 'action_items'] as const,
      },
      decisionAuthority: ['Asset classification', 'Disposition recommendations'],
      reportingTo: 'Clinical Engineer',
      icon: 'Database',
    },
    {
      id: 'vendor_coordinator',
      category: 'analyst' as const,
      name: 'Vendor Coordinator',
      shortName: 'Vendor Coord',
      description: 'Coordinates OEM service and vendor relationships',
      responsibilities: ['OEM scheduling', 'Contract management', 'Invoice review', 'Performance tracking'],
      focusAreas: ['Service agreements', 'Response times', 'Cost management', 'Vendor performance'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['business_impact', 'action_items'] as const,
      },
      decisionAuthority: ['Service scheduling', 'Invoice approval'],
      reportingTo: 'Clinical Engineer',
      icon: 'Users',
    },
  ],

  // =============================================================================
  // PARTS CATALOG
  // =============================================================================
  partsCatalog: {
    categories: [
      {
        id: 'imaging_parts',
        name: 'Diagnostic Imaging Parts',
        description: 'Parts for MRI, CT, X-ray, and ultrasound systems',
        parts: [
          { id: 'XRAY-TUBE-001', partNumber: 'XRAY-TUBE-001', name: 'X-Ray Tube', description: 'Replacement X-ray tube for CT and radiographic systems', category: 'imaging_parts', manufacturer: 'GE Healthcare', unitCost: 75000, currency: 'USD', leadTime: { value: 2, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['xray_system', 'ct_scanner'], failureModes: ['tube_failure', 'arc_failure'] },
          { id: 'CT-DET-001', partNumber: 'CT-DET-001', name: 'CT Detector Module', description: 'Solid state detector module for CT scanner', category: 'imaging_parts', manufacturer: 'Siemens', unitCost: 45000, currency: 'USD', leadTime: { value: 4, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['ct_scanner'], failureModes: ['detector_failure', 'calibration_drift'] },
          { id: 'MRI-COIL-001', partNumber: 'MRI-COIL-001', name: 'MRI RF Coil (Head)', description: 'Head receive coil for MRI scanner', category: 'imaging_parts', manufacturer: 'Philips', unitCost: 25000, currency: 'USD', leadTime: { value: 3, unit: 'weeks' as const }, criticalityLevel: 'essential' as const, compatibleAssetTypes: ['mri_scanner'], failureModes: ['coil_damage', 'snr_degradation'] },
          { id: 'US-TRANS-001', partNumber: 'US-TRANS-001', name: 'Ultrasound Transducer (Linear)', description: 'Linear array ultrasound transducer', category: 'imaging_parts', manufacturer: 'GE Healthcare', unitCost: 8000, currency: 'USD', leadTime: { value: 1, unit: 'weeks' as const }, criticalityLevel: 'essential' as const, compatibleAssetTypes: ['ultrasound'], failureModes: ['element_failure', 'cable_damage'] },
        ],
      },
      {
        id: 'life_support_parts',
        name: 'Life Support Equipment Parts',
        description: 'Parts for ventilators, defibrillators, and anesthesia machines',
        parts: [
          { id: 'VENT-FLOW-001', partNumber: 'VENT-FLOW-001', name: 'Ventilator Flow Sensor', description: 'Hot wire anemometer flow sensor for ventilator', category: 'life_support_parts', manufacturer: 'Hamilton', unitCost: 450, currency: 'USD', leadTime: { value: 3, unit: 'days' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['ventilator'], failureModes: ['sensor_drift', 'contamination'] },
          { id: 'VENT-VALVE-001', partNumber: 'VENT-VALVE-001', name: 'Exhalation Valve Assembly', description: 'Proportional exhalation valve for ventilator', category: 'life_support_parts', manufacturer: 'Draeger', unitCost: 1200, currency: 'USD', leadTime: { value: 1, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['ventilator'], failureModes: ['valve_leak', 'diaphragm_failure'] },
          { id: 'DEFIB-PAD-001', partNumber: 'DEFIB-PAD-001', name: 'Defibrillator Electrode Pads', description: 'Adult multifunction electrode pads', category: 'life_support_parts', manufacturer: 'Philips', unitCost: 45, currency: 'USD', leadTime: { value: 1, unit: 'days' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['defibrillator'], failureModes: ['gel_degradation', 'expiration'], averageLifespan: { value: 24, unit: 'months' as const } },
          { id: 'DEFIB-BAT-001', partNumber: 'DEFIB-BAT-001', name: 'Defibrillator Battery', description: 'Lithium-ion battery pack for defibrillator', category: 'life_support_parts', manufacturer: 'Zoll', unitCost: 350, currency: 'USD', leadTime: { value: 2, unit: 'days' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['defibrillator'], failureModes: ['capacity_loss', 'cell_failure'], averageLifespan: { value: 4, unit: 'years' as const } },
          { id: 'ANES-VAP-001', partNumber: 'ANES-VAP-001', name: 'Anesthesia Vaporizer', description: 'Sevoflurane anesthetic vaporizer', category: 'life_support_parts', manufacturer: 'GE Healthcare', unitCost: 8500, currency: 'USD', leadTime: { value: 2, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['anesthesia_machine'], failureModes: ['output_drift', 'fill_port_leak'] },
        ],
      },
      {
        id: 'monitoring_parts',
        name: 'Patient Monitoring Parts',
        description: 'Parts for patient monitors and accessories',
        parts: [
          { id: 'MON-SPO2-001', partNumber: 'MON-SPO2-001', name: 'SpO2 Sensor (Reusable)', description: 'Adult reusable finger SpO2 sensor', category: 'monitoring_parts', manufacturer: 'Masimo', unitCost: 250, currency: 'USD', leadTime: { value: 2, unit: 'days' as const }, criticalityLevel: 'essential' as const, compatibleAssetTypes: ['patient_monitor'], failureModes: ['led_degradation', 'cable_damage'] },
          { id: 'MON-ECG-001', partNumber: 'MON-ECG-001', name: 'ECG Leadwire Set', description: '5-lead ECG cable set', category: 'monitoring_parts', manufacturer: 'Philips', unitCost: 85, currency: 'USD', leadTime: { value: 1, unit: 'days' as const }, criticalityLevel: 'essential' as const, compatibleAssetTypes: ['patient_monitor'], failureModes: ['cable_break', 'connector_damage'] },
          { id: 'MON-NIBP-001', partNumber: 'MON-NIBP-001', name: 'NIBP Cuff Set', description: 'Adult blood pressure cuff set', category: 'monitoring_parts', manufacturer: 'GE Healthcare', unitCost: 45, currency: 'USD', leadTime: { value: 1, unit: 'days' as const }, criticalityLevel: 'standard' as const, compatibleAssetTypes: ['patient_monitor'], failureModes: ['bladder_leak', 'hose_damage'] },
          { id: 'MON-BAT-001', partNumber: 'MON-BAT-001', name: 'Monitor Battery', description: 'Lithium-ion battery for patient monitor', category: 'monitoring_parts', manufacturer: 'Philips', unitCost: 180, currency: 'USD', leadTime: { value: 2, unit: 'days' as const }, criticalityLevel: 'essential' as const, compatibleAssetTypes: ['patient_monitor'], failureModes: ['capacity_loss'], averageLifespan: { value: 3, unit: 'years' as const } },
        ],
      },
      {
        id: 'infusion_parts',
        name: 'Drug Delivery Parts',
        description: 'Parts for infusion pumps and syringe pumps',
        parts: [
          { id: 'INF-MECH-001', partNumber: 'INF-MECH-001', name: 'Infusion Pump Mechanism', description: 'Linear peristaltic pump mechanism', category: 'infusion_parts', manufacturer: 'BD', unitCost: 650, currency: 'USD', leadTime: { value: 1, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['infusion_pump'], failureModes: ['mechanism_wear', 'motor_failure'] },
          { id: 'INF-BAT-001', partNumber: 'INF-BAT-001', name: 'Infusion Pump Battery', description: 'NiMH battery pack for infusion pump', category: 'infusion_parts', manufacturer: 'BD', unitCost: 95, currency: 'USD', leadTime: { value: 2, unit: 'days' as const }, criticalityLevel: 'essential' as const, compatibleAssetTypes: ['infusion_pump'], failureModes: ['capacity_loss'], averageLifespan: { value: 2, unit: 'years' as const } },
          { id: 'SYR-DRIVE-001', partNumber: 'SYR-DRIVE-001', name: 'Syringe Pump Drive Assembly', description: 'Lead screw drive assembly for syringe pump', category: 'infusion_parts', manufacturer: 'BD', unitCost: 450, currency: 'USD', leadTime: { value: 1, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['syringe_pump'], failureModes: ['drive_wear', 'position_error'] },
        ],
      },
      {
        id: 'laboratory_parts',
        name: 'Laboratory Analyzer Parts',
        description: 'Parts for clinical laboratory analyzers',
        parts: [
          { id: 'LAB-PROBE-001', partNumber: 'LAB-PROBE-001', name: 'Sample Probe Assembly', description: 'Aspiration/dispense probe with level sensing', category: 'laboratory_parts', manufacturer: 'Roche', unitCost: 850, currency: 'USD', leadTime: { value: 1, unit: 'weeks' as const }, criticalityLevel: 'critical' as const, compatibleAssetTypes: ['lab_analyzer'], failureModes: ['probe_clog', 'level_sensor_failure'] },
          { id: 'LAB-LAMP-001', partNumber: 'LAB-LAMP-001', name: 'Spectrophotometer Lamp', description: 'Halogen lamp for chemistry analyzer', category: 'laboratory_parts', manufacturer: 'Beckman Coulter', unitCost: 120, currency: 'USD', leadTime: { value: 2, unit: 'days' as const }, criticalityLevel: 'essential' as const, compatibleAssetTypes: ['lab_analyzer'], failureModes: ['lamp_burnout', 'intensity_degradation'], averageLifespan: { value: 2000, unit: 'hours' as const } },
          { id: 'LAB-PUMP-001', partNumber: 'LAB-PUMP-001', name: 'Reagent Pump', description: 'Peristaltic reagent pump', category: 'laboratory_parts', manufacturer: 'Abbott', unitCost: 380, currency: 'USD', leadTime: { value: 1, unit: 'weeks' as const }, criticalityLevel: 'essential' as const, compatibleAssetTypes: ['lab_analyzer'], failureModes: ['tubing_wear', 'motor_failure'] },
        ],
      },
    ],
  },

  // =============================================================================
  // ROOT CAUSE LIBRARY
  // =============================================================================
  rootCauseLibrary: {
    categories: [
      {
        id: 'calibration',
        name: 'Calibration Issues',
        description: 'Root causes related to measurement accuracy and calibration',
        icon: 'Target',
        color: '#8b5cf6',
        rootCauses: [
          {
            id: 'calibration_drift',
            name: 'Calibration Drift',
            description: 'Gradual deviation of device measurements from reference values',
            category: 'calibration',
            symptoms: ['Out-of-tolerance readings', 'QC failures', 'Inconsistent measurements', 'Clinical complaints'],
            diagnosticSteps: [
              { order: 1, action: 'Compare device reading to NIST-traceable reference standard', expectedResult: 'Quantify deviation from reference', tools: ['Calibration standard', 'Reference equipment'], duration: 30, skillLevel: 'intermediate' as const },
              { order: 2, action: 'Perform multi-point calibration check across measurement range', expectedResult: 'Verify linearity and accuracy', tools: ['Multi-level standards'], duration: 45, skillLevel: 'advanced' as const },
              { order: 3, action: 'Review calibration history for drift pattern', expectedResult: 'Identify if gradual or sudden drift', tools: ['CMMS records'], duration: 15, skillLevel: 'intermediate' as const },
            ],
            preventiveActions: ['Establish appropriate calibration intervals', 'Track calibration trends', 'Implement calibration alerts', 'Use quality standards'],
            typicalProbability: 0.20,
            affectedComponents: ['Sensors', 'Transducers', 'Measuring elements'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 30, typical: 60, max: 180, unit: 'minutes' as const },
            costRange: { min: 50, max: 500, currency: 'USD' },
            industryReferences: ['ISO 17025', 'TJC EC.02.04.01'],
            complianceImplications: ['Joint Commission findings', 'State inspection citations'],
          },
          {
            id: 'sensor_contamination',
            name: 'Sensor Contamination',
            description: 'Biological or environmental contamination affecting sensor accuracy',
            category: 'calibration',
            symptoms: ['Erratic readings', 'Slow response', 'Visible contamination', 'QC failures'],
            diagnosticSteps: [
              { order: 1, action: 'Visual inspection of sensor elements', expectedResult: 'Identify visible contamination', tools: ['Magnification', 'Good lighting'], duration: 10, skillLevel: 'basic' as const },
              { order: 2, action: 'Clean sensor per manufacturer protocol', expectedResult: 'Restore clean sensor surface', tools: ['Approved cleaning supplies'], duration: 15, skillLevel: 'basic' as const },
              { order: 3, action: 'Perform post-cleaning verification', expectedResult: 'Readings within specification', tools: ['QC materials'], duration: 20, skillLevel: 'intermediate' as const },
            ],
            preventiveActions: ['Regular sensor cleaning schedule', 'Proper handling training', 'Use protective covers', 'Replace consumable sensors per schedule'],
            typicalProbability: 0.15,
            affectedComponents: ['Sensors', 'Electrodes', 'Optical components'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 15, typical: 30, max: 60, unit: 'minutes' as const },
            costRange: { min: 20, max: 200, currency: 'USD' },
          },
        ],
      },
      {
        id: 'mechanical',
        name: 'Mechanical Failures',
        description: 'Root causes related to mechanical component wear and damage',
        icon: 'Settings',
        color: '#ef4444',
        rootCauses: [
          {
            id: 'pump_mechanism_wear',
            name: 'Pump Mechanism Wear',
            description: 'Wear in infusion pump mechanisms affecting flow accuracy',
            category: 'mechanical',
            symptoms: ['Flow rate variance', 'Occlusion alarms without obstruction', 'Unusual motor sounds', 'Inconsistent delivery'],
            diagnosticSteps: [
              { order: 1, action: 'Perform flow rate accuracy test', expectedResult: 'Flow within ±5% of set rate', tools: ['Flow analyzer', 'Reference fluid'], duration: 30, skillLevel: 'intermediate' as const },
              { order: 2, action: 'Inspect pump mechanism for wear', expectedResult: 'Check rollers, tubing bed, drive components', tools: ['Inspection light', 'Manufacturer checklist'], duration: 20, skillLevel: 'intermediate' as const },
              { order: 3, action: 'Test occlusion detection thresholds', expectedResult: 'Alarm triggers at specified pressure', tools: ['Pressure simulator'], duration: 20, skillLevel: 'advanced' as const },
            ],
            preventiveActions: ['Follow PM schedule', 'Replace wear components proactively', 'Train staff on proper loading', 'Use quality tubing sets'],
            typicalProbability: 0.12,
            affectedComponents: ['Pump mechanism', 'Rollers', 'Motor', 'Drive assembly'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 30, typical: 90, max: 240, unit: 'minutes' as const },
            costRange: { min: 100, max: 1500, currency: 'USD' },
            industryReferences: ['IEC 60601-2-24', 'AAMI TIR35'],
            complianceImplications: ['Patient safety critical', 'FDA quality requirements'],
          },
          {
            id: 'ventilator_valve_malfunction',
            name: 'Ventilator Valve Malfunction',
            description: 'Solenoid or proportional valve issues affecting ventilator performance',
            category: 'mechanical',
            symptoms: ['Flow delivery issues', 'Pressure waveform abnormalities', 'Alarm conditions', 'Audible air leaks'],
            diagnosticSteps: [
              { order: 1, action: 'Perform ventilator self-test', expectedResult: 'Note any failure codes', tools: ['Built-in self-test'], duration: 10, skillLevel: 'basic' as const },
              { order: 2, action: 'Test valve operation with test lung', expectedResult: 'Proper opening/closing, no leaks', tools: ['Test lung', 'Flow analyzer'], duration: 30, skillLevel: 'advanced' as const },
              { order: 3, action: 'Inspect valve components for wear', expectedResult: 'Check diaphragms, seats, solenoid operation', tools: ['Valve repair kit'], duration: 45, skillLevel: 'advanced' as const, safetyPrecautions: ['Device out of clinical use', 'Follow cleaning protocol'] },
            ],
            preventiveActions: ['Scheduled PM per manufacturer', 'Prompt replacement of expiratory valves', 'Regular leak testing', 'Proper cleaning and disinfection'],
            typicalProbability: 0.08,
            affectedComponents: ['Inspiratory valve', 'Expiratory valve', 'PEEP valve', 'Solenoids'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 30, typical: 120, max: 360, unit: 'minutes' as const },
            costRange: { min: 200, max: 3000, currency: 'USD' },
            industryReferences: ['IEC 60601-2-12', 'ISO 80601-2-12'],
            complianceImplications: ['Life-support critical', 'Immediate patient safety impact'],
          },
        ],
      },
      {
        id: 'electrical',
        name: 'Electrical Failures',
        description: 'Root causes related to power and electronic component failures',
        icon: 'Zap',
        color: '#f59e0b',
        rootCauses: [
          {
            id: 'power_supply_degradation',
            name: 'Power Supply Degradation',
            description: 'Internal power supply component wear affecting device operation',
            category: 'electrical',
            symptoms: ['Intermittent power-on issues', 'Unexpected shutdown', 'Display flickering', 'Charging issues'],
            diagnosticSteps: [
              { order: 1, action: 'Measure power supply output voltages', expectedResult: 'All rails within ±5% of specification', tools: ['Multimeter'], duration: 20, skillLevel: 'intermediate' as const },
              { order: 2, action: 'Visual inspection for capacitor bulging/leakage', expectedResult: 'All capacitors appear normal', tools: ['Visual inspection', 'Magnification'], duration: 15, skillLevel: 'intermediate' as const },
              { order: 3, action: 'Test under load conditions', expectedResult: 'Voltages stable under typical load', tools: ['Load tester', 'Oscilloscope'], duration: 30, skillLevel: 'advanced' as const },
            ],
            preventiveActions: ['Operating temperature control', 'Regular internal cleaning', 'PM inspections', 'Proactive replacement in critical devices'],
            typicalProbability: 0.10,
            affectedComponents: ['Capacitors', 'Regulators', 'Transformers', 'Rectifiers'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 60, typical: 180, max: 480, unit: 'minutes' as const },
            costRange: { min: 300, max: 2500, currency: 'USD' },
            complianceImplications: ['IEC 62353 requirements', 'Electrical safety'],
          },
          {
            id: 'battery_degradation',
            name: 'Battery Capacity Degradation',
            description: 'Reduced battery capacity affecting portable device operation',
            category: 'electrical',
            symptoms: ['Reduced runtime', 'Low battery alarms', 'Unexpected shutdown', 'Battery health warnings'],
            diagnosticSteps: [
              { order: 1, action: 'Perform battery capacity test', expectedResult: 'Measure capacity vs rated', tools: ['Battery analyzer'], duration: 30, skillLevel: 'intermediate' as const },
              { order: 2, action: 'Check charge/discharge cycles in service history', expectedResult: 'Compare to expected lifespan', tools: ['CMMS records'], duration: 10, skillLevel: 'basic' as const },
              { order: 3, action: 'Verify charging system function', expectedResult: 'Proper charging voltage and current', tools: ['Multimeter'], duration: 20, skillLevel: 'intermediate' as const },
            ],
            preventiveActions: ['Scheduled battery replacement', 'Track battery age and cycles', 'Proper charging practices', 'Temperature management'],
            typicalProbability: 0.18,
            affectedComponents: ['Battery cells', 'Charging circuit', 'Battery contacts'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 15, typical: 30, max: 60, unit: 'minutes' as const },
            costRange: { min: 50, max: 500, currency: 'USD' },
            complianceImplications: ['Life support device reliability'],
          },
        ],
      },
      {
        id: 'software',
        name: 'Software Issues',
        description: 'Root causes related to firmware and software failures',
        icon: 'Code',
        color: '#06b6d4',
        rootCauses: [
          {
            id: 'firmware_bug',
            name: 'Firmware Bug',
            description: 'Software defect causing incorrect device behavior',
            category: 'software',
            symptoms: ['Reproducible unexpected behavior', 'System freezes', 'Incorrect calculations', 'Known manufacturer issue'],
            diagnosticSteps: [
              { order: 1, action: 'Document exact steps to reproduce issue', expectedResult: 'Clear reproduction steps documented', tools: ['Documentation template'], duration: 30, skillLevel: 'intermediate' as const },
              { order: 2, action: 'Check current firmware version', expectedResult: 'Compare to latest available', tools: ['Device settings'], duration: 5, skillLevel: 'basic' as const },
              { order: 3, action: 'Review manufacturer bulletins', expectedResult: 'Identify if known issue with available fix', tools: ['Manufacturer website', 'Technical support'], duration: 30, skillLevel: 'intermediate' as const },
            ],
            preventiveActions: ['Subscribe to manufacturer alerts', 'Implement firmware update policy', 'Test updates before deployment', 'Maintain version inventory'],
            typicalProbability: 0.08,
            affectedComponents: ['Firmware', 'Control software', 'Embedded systems'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 30, typical: 120, max: 480, unit: 'minutes' as const },
            costRange: { min: 0, max: 500, currency: 'USD' },
            complianceImplications: ['FDA recall implications', 'MDR reporting'],
          },
          {
            id: 'network_connectivity',
            name: 'Network Connectivity Failure',
            description: 'Loss of network connectivity affecting device integration',
            category: 'software',
            symptoms: ['Data not transmitting', 'Disconnection alarms', 'Integration errors', 'Missing patient data'],
            diagnosticSteps: [
              { order: 1, action: 'Check physical network connection', expectedResult: 'Verify cable and link lights', tools: ['Visual inspection', 'Cable tester'], duration: 10, skillLevel: 'basic' as const },
              { order: 2, action: 'Verify network configuration', expectedResult: 'IP address, subnet, gateway correct', tools: ['Network settings'], duration: 15, skillLevel: 'intermediate' as const },
              { order: 3, action: 'Test connectivity to server/EMR', expectedResult: 'Successful ping and data transfer', tools: ['Network diagnostic tools'], duration: 20, skillLevel: 'advanced' as const },
            ],
            preventiveActions: ['Monitor network status', 'Document network settings', 'Regular connectivity verification', 'Redundant connections for critical devices'],
            typicalProbability: 0.12,
            affectedComponents: ['Network interface', 'Cables', 'Switch ports', 'Server connectivity'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 15, typical: 60, max: 240, unit: 'minutes' as const },
            costRange: { min: 0, max: 300, currency: 'USD' },
          },
        ],
      },
    ],
    commonPatterns: [
      {
        id: 'calibration_cascade',
        name: 'Calibration-Quality-Patient Impact',
        description: 'Undetected calibration drift leads to quality failures and potential patient harm',
        sequence: ['Sensor aging/contamination', 'Gradual calibration drift', 'Inaccurate measurements', 'QC failures', 'Clinical decisions on inaccurate data', 'Patient safety event'],
        indicators: ['Trending calibration values', 'Intermittent QC failures', 'Staff observations'],
        preventionStrategy: 'Implement calibration trending with drift alerts and appropriate verification intervals',
      },
      {
        id: 'battery_chain',
        name: 'Battery-Power-Safety Chain',
        description: 'Battery degradation leads to backup power failure during critical use',
        sequence: ['Battery capacity decrease', 'Reduced runtime', 'Unexpected shutdown during use', 'Patient safety event'],
        indicators: ['Decreasing battery runtime', 'Battery health warnings', 'Age of battery'],
        preventionStrategy: 'Track battery health metrics and replace proactively based on cycle count and age',
      },
      {
        id: 'aging_equipment',
        name: 'Aging Equipment Cascade',
        description: 'Multiple component failures due to equipment age exceeding useful life',
        sequence: ['Component aging', 'Increased failure frequency', 'Parts obsolescence', 'Extended downtime', 'Patient care impact'],
        indicators: ['Increasing service calls', 'Longer repair times', 'Parts availability issues'],
        preventionStrategy: 'Lifecycle management with proactive replacement planning',
      },
    ],
  },

  // =============================================================================
  // LABOR RATES
  // =============================================================================
  laborRates: {
    roles: [
      { roleId: 'bmet_i', name: 'BMET I / Associate Technician', hourlyRate: 35, currency: 'USD', certifications: ['ACI'] },
      { roleId: 'bmet_ii', name: 'BMET II / Technician', hourlyRate: 45, currency: 'USD', certifications: ['CBET'] },
      { roleId: 'bmet_iii', name: 'BMET III / Senior Technician', hourlyRate: 55, currency: 'USD', certifications: ['CBET', 'CRES'] },
      { roleId: 'calibration_specialist', name: 'Calibration Specialist', hourlyRate: 55, currency: 'USD', certifications: ['ASQ CQT'] },
      { roleId: 'imaging_specialist', name: 'Imaging Specialist', hourlyRate: 70, currency: 'USD', certifications: ['CRES', 'CT/MR Certified'] },
      { roleId: 'life_support_specialist', name: 'Life Support Specialist', hourlyRate: 65, currency: 'USD', certifications: ['CBET', 'Life Support'] },
      { roleId: 'biomedical_engineer', name: 'Biomedical Engineer', hourlyRate: 85, currency: 'USD', certifications: ['PE', 'CBET'] },
      { roleId: 'clinical_engineer', name: 'Clinical Engineer', hourlyRate: 95, currency: 'USD', certifications: ['CCE', 'PE'] },
      { roleId: 'it_specialist', name: 'Medical IT Specialist', hourlyRate: 65, currency: 'USD', certifications: ['CompTIA', 'HIPAA'] },
      { roleId: 'oem_fse', name: 'OEM Field Service Engineer', hourlyRate: 175, currency: 'USD', certifications: ['Manufacturer Certified'] },
      { roleId: 'vendor_coordinator', name: 'Vendor Coordinator', hourlyRate: 45, currency: 'USD', certifications: [] },
      { roleId: 'asset_manager', name: 'Asset Manager', hourlyRate: 55, currency: 'USD', certifications: ['CMRP'] },
    ],
    overtimeMultiplier: 1.5,
    emergencyMultiplier: 2.5,
    weekendMultiplier: 1.75,
  },

  // =============================================================================
  // VISUALIZATION
  // =============================================================================
  visualization: {
    primaryColor: '#06b6d4',
    secondaryColor: '#8b5cf6',
    healthyColor: '#10b981',
    warningColor: '#f59e0b',
    criticalColor: '#ef4444',
    chartDefaults: {
      showGrid: true,
      showLegend: true,
      animationDuration: 300,
    },
  },

  // =============================================================================
  // NARRATIVE TEMPLATES
  // =============================================================================
  narrativeTemplates: [
    {
      id: 'device_health_summary',
      name: 'Device Health Summary',
      context: 'asset_health',
      audience: ['manager', 'engineer', 'technician'],
      template: 'Your {assetType} "{assetName}" in {location} is currently {healthStatus}. {trendDescription} Calibration status: {calibrationStatus}. Last PM: {lastPmDate}. Next scheduled {maintenance}: {nextServiceDate}. Device age: {deviceAge} years. Patient safety impact: {safetyImpact}.',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'location', type: 'terminology' },
        { name: 'healthStatus', type: 'terminology' },
        { name: 'trendDescription', type: 'metric' },
        { name: 'calibrationStatus', type: 'metric' },
        { name: 'lastPmDate', type: 'time' },
        { name: 'maintenance', type: 'terminology' },
        { name: 'nextServiceDate', type: 'time' },
        { name: 'deviceAge', type: 'metric' },
        { name: 'safetyImpact', type: 'metric' },
      ],
    },
    {
      id: 'patient_safety_alert',
      name: 'Patient Safety Alert',
      context: 'anomaly',
      audience: ['manager', 'engineer'],
      template: 'PATIENT SAFETY ALERT: {assetType} "{assetName}" in {location} has {anomaly} detected. FDA Device Class: {deviceClass}. Patient safety impact: {safetyImpact}. Immediate action required: {recommendation}. Contact Clinical Engineering immediately. Reference: {referenceNumber}.',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'location', type: 'terminology' },
        { name: 'anomaly', type: 'terminology' },
        { name: 'deviceClass', type: 'metric' },
        { name: 'safetyImpact', type: 'metric' },
        { name: 'recommendation', type: 'metric' },
        { name: 'referenceNumber', type: 'metric' },
      ],
    },
    {
      id: 'compliance_status',
      name: 'Compliance Status Summary',
      context: 'asset_health',
      audience: ['executive', 'manager'],
      template: 'Compliance Summary for {facilityName}: PM Compliance: {pmCompliance}%. Calibration Current: {calibrationCompliance}%. Electrical Safety Current: {safetyCompliance}%. Open Recalls: {openRecalls}. Next accreditation survey: {surveyDate}. Action items: {actionItems}.',
      variables: [
        { name: 'facilityName', type: 'asset' },
        { name: 'pmCompliance', type: 'percentage' },
        { name: 'calibrationCompliance', type: 'percentage' },
        { name: 'safetyCompliance', type: 'percentage' },
        { name: 'openRecalls', type: 'metric' },
        { name: 'surveyDate', type: 'time' },
        { name: 'actionItems', type: 'metric' },
      ],
    },
    {
      id: 'service_recommendation',
      name: 'Service Recommendation',
      context: 'prediction',
      audience: ['engineer', 'technician'],
      template: 'Service Recommended for {assetType} "{assetName}": {serviceType} is recommended within {timeframe}. Prediction confidence: {confidence}%. Key indicators: {indicators}. Estimated service time: {estimatedTime}. Parts required: {partsNeeded}. Estimated cost: {estimatedCost}. If deferred: {deferralRisk}.',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'serviceType', type: 'terminology' },
        { name: 'timeframe', type: 'time' },
        { name: 'confidence', type: 'percentage' },
        { name: 'indicators', type: 'metric' },
        { name: 'estimatedTime', type: 'time' },
        { name: 'partsNeeded', type: 'metric' },
        { name: 'estimatedCost', type: 'currency' },
        { name: 'deferralRisk', type: 'metric' },
      ],
    },
    {
      id: 'root_cause_analysis',
      name: 'Root Cause Analysis',
      context: 'prediction',
      audience: ['engineer', 'manager'],
      template: 'Root Cause Analysis for {assetName} {malfunctionType}: Primary root cause: {rootCause} ({confidence}% confidence). Contributing factors: {contributingFactors}. FDA reportability: {fdaReportable}. Patient impact: {patientImpact}. Corrective actions: {correctiveActions}. Preventive measures: {preventiveMeasures}. Documentation reference: {documentReference}.',
      variables: [
        { name: 'assetName', type: 'asset' },
        { name: 'malfunctionType', type: 'terminology' },
        { name: 'rootCause', type: 'metric' },
        { name: 'confidence', type: 'percentage' },
        { name: 'contributingFactors', type: 'metric' },
        { name: 'fdaReportable', type: 'metric' },
        { name: 'patientImpact', type: 'metric' },
        { name: 'correctiveActions', type: 'metric' },
        { name: 'preventiveMeasures', type: 'metric' },
        { name: 'documentReference', type: 'metric' },
      ],
    },
  ],

  // =============================================================================
  // FEATURES
  // =============================================================================
  features: {
    enableBenchmarking: true,
    enableWhatIf: false, // Healthcare focuses on compliance, not what-if scenarios
    enableCollaboration: true,
    enableCompliance: true,
    enableKnowledgeCapture: true,
  },

  // =============================================================================
  // DEFAULTS
  // =============================================================================
  defaults: {
    timeRange: '30d',
    refreshInterval: 60,
    defaultDashboard: 'command-center',
  },
}
